<template>
  <div>
    <!-- Back Button & Actions -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <b-button
        variant="outline-secondary"
        :to="{ name: 'pickup-roadmaps' }"
      >
        <feather-icon
          icon="ArrowLeftIcon"
          class="mr-50"
        />
        Retour
      </b-button>

    </div>

    <div
      v-if="isLoading"
      class="text-center py-5"
    >
      <b-spinner
        variant="primary"
        style="width: 3rem; height: 3rem;"
      />
    </div>

    <template v-else>
      <!-- Info Cards -->
      <b-row class="mb-2">
        <!-- Main Info -->
        <b-col
          lg="4"
          md="6"
        >
          <b-card class="h-100">
            <h4 class="mb-2">
              <feather-icon
                icon="FileTextIcon"
                class="mr-50"
              />
              Feuille #{{ pickupRoadmap.id }}
            </h4>
            <p class="mb-1">
              <strong>Statut:</strong>
              <b-badge
                :variant="getStatusVariant(pickupRoadmap.status)"
                class="ml-50"
              >
                {{ pickupRoadmap.status_display }}
              </b-badge>
            </p>
            <p class="mb-1">
              <strong>Créé le:</strong>
              {{ formatDate(pickupRoadmap.created_at) }}
            </p>
            <p
              v-if="pickupRoadmap.started_at"
              class="mb-1"
            >
              <strong>Démarré le:</strong>
              {{ formatDate(pickupRoadmap.started_at) }}
            </p>
            <p
              v-if="pickupRoadmap.completed_at"
              class="mb-1"
            >
              <strong>Terminé le:</strong>
              {{ formatDate(pickupRoadmap.completed_at) }}
            </p>
          </b-card>
        </b-col>

        <!-- Driver Info -->
        <b-col
          lg="4"
          md="6"
        >
          <b-card class="h-100">
            <h4 class="mb-2">
              <feather-icon
                icon="UserIcon"
                class="mr-50"
              />
              Livreur
            </h4>
            <p class="mb-1">
              <strong>Nom:</strong>
              {{ pickupRoadmap.driver_name }}
            </p>
            <p class="mb-1">
              <strong>Téléphone:</strong>
              <a :href="`tel:${pickupRoadmap.driver_phone}`">
                {{ pickupRoadmap.driver_phone }}
              </a>
            </p>
            <p
              v-if="pickupRoadmap.car_info"
              class="mb-1"
            >
              <strong>Voiture:</strong>
              {{ pickupRoadmap.car_info }}
            </p>
          </b-card>
        </b-col>

        <!-- Repository Info -->
        <b-col
          lg="4"
          md="6"
        >
          <b-card class="h-100">
            <h4 class="mb-2">
              <feather-icon
                icon="HomeIcon"
                class="mr-50"
              />
              Dépôt
            </h4>
            <p class="mb-1">
              <strong>Nom:</strong>
              {{ pickupRoadmap.repository_name }}
            </p>
            <p class="mb-1">
              <strong>Adresse:</strong>
              {{ pickupRoadmap.repository_address }}
            </p>
          </b-card>
        </b-col>
      </b-row>

      <!-- Statistics -->
      <b-row class="mb-2">
        <b-col
          sm="6"
          md="3"
        >
          <b-card class="text-center bg-light-primary">
            <h2 class="text-primary mb-0">
              {{ pickupRoadmap.orders_count }}
            </h2>
            <small class="text-muted">Total Colis</small>
          </b-card>
        </b-col>
        <b-col
          sm="6"
          md="3"
        >
          <b-card class="text-center bg-light-warning">
            <h2 class="text-warning mb-0">
              {{ getOrderCountByStatus(0) }}
            </h2>
            <small class="text-muted">En Attente</small>
          </b-card>
        </b-col>
        <b-col
          sm="6"
          md="3"
        >
          <b-card class="text-center bg-light-info">
            <h2 class="text-info mb-0">
              {{ getOrderCountByStatus(1) }}
            </h2>
            <small class="text-muted">En Pickup</small>
          </b-card>
        </b-col>
        <b-col
          sm="6"
          md="3"
        >
          <b-card class="text-center bg-light-success">
            <h2 class="text-success mb-0">
              {{ getPickedUpCount() }}
            </h2>
            <small class="text-muted">Ramassé</small>
          </b-card>
        </b-col>
      </b-row>

      <!-- Notes -->
      <b-card
        v-if="pickupRoadmap.notes"
        class="mb-2"
      >
        <h4 class="mb-1">
          <feather-icon
            icon="Edit3Icon"
            class="mr-50"
          />
          Notes
        </h4>
        <p class="mb-0">
          {{ pickupRoadmap.notes }}
        </p>
      </b-card>

      <!-- Orders Table -->
      <b-card>
        <h4 class="mb-2">
          <feather-icon
            icon="PackageIcon"
            class="mr-50"
          />
          Colis ({{ orders.length }})
        </h4>

        <b-table
          responsive
          :items="orders"
          :fields="orderFields"
          show-empty
          empty-text="Aucun colis"
          class="mb-0"
        >
          <!-- Barcode Column -->
          <template #cell(bar_code)="data">
            <router-link :to="{ name: 'order-show', params: { id: data.item.id } }">
              {{ data.item.bar_code }}
            </router-link>
          </template>

          <!-- Status Column -->
          <template #cell(status)="data">
            <b-badge :variant="getOrderStatusVariant(data.item.status)">
              {{ data.item.status_display }}
            </b-badge>
          </template>

          <!-- Supplier Column -->
          <template #cell(supplier)="data">
            {{ data.item.supplier_name }}
          </template>

          <!-- Client Column -->
          <template #cell(client)="data">
            <div>
              <div>{{ data.item.client_name }}</div>
              <small class="text-muted">{{ data.item.client_phone }}</small>
            </div>
          </template>

          <!-- Governorate Column -->
          <template #cell(governorate)="data">
            {{ data.item.governorate_name }}
          </template>

          <!-- Actions Column -->
          <template #cell(actions)="data">
            <b-button
              v-b-tooltip.hover
              title="Voir détails"
              variant="outline-primary"
              size="sm"
              :to="{ name: 'order-show', params: { id: data.item.id } }"
            >
              <feather-icon icon="EyeIcon" />
            </b-button>
          </template>
        </b-table>
      </b-card>
    </template>
  </div>
</template>

<script>
import {
  BCard,
  BButton,
  BTable,
  BBadge,
  BRow,
  BCol,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'PickupRoadmapShow',
  components: {
    BCard,
    BButton,
    BTable,
    BBadge,
    BRow,
    BCol,
    BSpinner,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      isLoading: false,
      pickupRoadmap: {},
      orders: [],

      orderFields: [
        { key: 'bar_code', label: 'Code Barre', sortable: true },
        { key: 'status', label: 'Statut' },
        { key: 'supplier', label: 'Fournisseur' },
        { key: 'client', label: 'Client' },
        { key: 'governorate', label: 'Gouvernorat' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  computed: {
    id() {
      return this.$route.params.id
    },
  },
  created() {
    this.loadPickupRoadmap()
  },
  methods: {
    async loadPickupRoadmap() {
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/pickups/roadmap/${this.id}/`)
        this.pickupRoadmap = data
        this.orders = data.orders || []
      } catch (error) {
        console.error('Error loading pickup roadmap:', error)
        this.showToast('danger', 'Erreur lors du chargement')
        this.$router.push({ name: 'pickup-roadmaps' })
      } finally {
        this.isLoading = false
      }
    },

    async startPickup() {
      try {
        await axios.post(`/api/pickups/roadmap/${this.id}/start/`)
        this.showToast('success', 'Feuille de ramassage démarrée')
        this.loadPickupRoadmap()
      } catch (error) {
        this.showToast('danger', error.response?.data?.detail || 'Erreur')
      }
    },

    async completePickup() {
      const confirmed = await this.$swal({
        title: 'Confirmer',
        text: 'Voulez-vous marquer cette feuille comme terminée ?',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Oui',
        cancelButtonText: 'Non',
      })

      if (!confirmed.isConfirmed) return

      try {
        await axios.post(`/api/pickups/roadmap/${this.id}/complete/`)
        this.showToast('success', 'Feuille de ramassage terminée')
        this.loadPickupRoadmap()
      } catch (error) {
        this.showToast('danger', error.response?.data?.detail || 'Erreur')
      }
    },

    async cancelPickup() {
      const confirmed = await this.$swal({
        title: 'Confirmer',
        text: 'Voulez-vous annuler cette feuille ?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, annuler',
        cancelButtonText: 'Non',
      })

      if (!confirmed.isConfirmed) return

      try {
        await axios.post(`/api/pickups/roadmap/${this.id}/cancel/`)
        this.showToast('success', 'Feuille de ramassage annulée')
        this.loadPickupRoadmap()
      } catch (error) {
        this.showToast('danger', error.response?.data?.detail || 'Erreur')
      }
    },

    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        in_progress: 'info',
        completed: 'success',
        cancelled: 'danger',
      }
      return variants[status] || 'secondary'
    },

    getOrderStatusVariant(status) {
      const variants = {
        0: 'warning', // En attente
        1: 'info', // En pickup
        2: 'primary', // Au dépôt
        3: 'info', // En cours
        4: 'secondary', // En attente 2
        5: 'success', // Livré
        6: 'success', // Livré payé
        7: 'danger', // Retour
        8: 'danger', // Retour en dépôt
        9: 'danger', // Retour payé
      }
      return variants[status] || 'secondary'
    },

    getOrderCountByStatus(status) {
      return this.orders.filter(order => order.status === status).length
    },

    getPickedUpCount() {
      // Count orders that have status > 1 (picked up)
      return this.orders.filter(order => order.status > 1).length
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    showToast(variant, text) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Notification',
          icon: 'InfoIcon',
          text,
          variant,
        },
      })
    },
  },
}
</script>

<style scoped>
.bg-light-primary {
  background-color: rgba(115, 103, 240, 0.12) !important;
}
.bg-light-success {
  background-color: rgba(40, 199, 111, 0.12) !important;
}
.bg-light-warning {
  background-color: rgba(255, 159, 67, 0.12) !important;
}
.bg-light-info {
  background-color: rgba(0, 207, 232, 0.12) !important;
}
</style>
